import styled from "@emotion/styled";
import { Box, Chip, Grid, Stack, type Theme, Typography, useTheme } from "@mui/material";
import { Button as MuiButton } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ActionButton, Flex, colors } from "@zfp-akademie-a-s/zfp-design";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { AddIcon, DeleteIcon, InfoIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { ArrowRightIcon, MinusIcon } from "Components/_Shared/Icons/Icons";
import { getListInsuranceRisksQueryKey, useDeleteInsuranceRisk, useUpdateInsuranceRisk } from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type Risk as RiskDto, type UpdateRiskBody } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { RISK_TYPE_RESOURCE_KEY } from "Infrastructure/Utils/InsuranceUtils";
import { type FC, useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { RecommendedAmountsTooltip } from "./RecommendedAmountsTooltip";
import { type RiskFormValues, type SerialQueue, buildUpdateRiskBody, createSerialQueue } from "./Risk.helpers";
import { ThumbsUpIcon } from "./ThumbsUpIcon";

type Props = {
  readonly analysisId: string;
  readonly versionId: string;
  readonly clientId: string;
  readonly risk: RiskDto;
};

type Form = RiskFormValues;

const StyledStack = styled(Stack)`
  background: ${colors.backgroundSecondary};
  padding: ${(props) => props.theme.spacing(Spacing.XL)};
  border-radius: ${(props) => props.theme.spacing(Radius.XL)};
`;

const getThumbsUpColor = (currentAmount: number, recommendedAmount: number, theme: Theme) => {
  if (currentAmount === 0) {
    if (recommendedAmount === 0) return theme.palette.success.main;
    return recommendedAmount > 0 ? theme.palette.error.main : theme.palette.warning.dark;
  }

  const diff = recommendedAmount - currentAmount;
  const percentDiff = diff / Math.abs(currentAmount);

  if (Math.abs(percentDiff) < 0.1) {
    return theme.palette.success.main;
  }
  if (-percentDiff > 0.1) {
    return theme.palette.warning.dark;
  }
  return theme.palette.error.main;
};

export const Risk: FC<Props> = ({ risk, analysisId, versionId, clientId }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { readOnly } = useContext(AnalysisDetailContext);
  const { t } = useResource();

  const [parametersExpanded, setParametersExpanded] = useState(false);

  const { mutateAsync: deleteInsuranceRisk } = useDeleteInsuranceRisk({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListInsuranceRisksQueryKey(analysisId, versionId, clientId) });
        toast.success("Riziko úspěšně smazáno.");
      },
    },
  });

  const { mutateAsync: updateInsuranceRisk } = useUpdateInsuranceRisk({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListInsuranceRisksQueryKey(analysisId, versionId, clientId) });
        toast.success("Riziko úspěšně upraveno.");
      },
    },
  });

  const defaultValues: Form = {
    currentAmount: risk?.currentAmount.value ?? ("0" as any),
    currentAgeTo: risk?.currentAgeTo ?? ("0" as any),
    recommendedAmount: risk?.recommendedAmount.value ?? ("0" as any),
    recommendedAgeTo: risk?.recommendedAgeTo ?? ("0" as any),
    requiredBaseAmount: risk?.requiredBaseAmount.value.value ?? ("0" as any),
    infinityRent: risk?.infinityRent.value.value ?? ("0" as any),
    requiredCoverageCompensation: risk?.requiredCoverageCompensation.value.value ?? ("0" as any),
  };

  const { reset, watch, control } = useForm({
    defaultValues,
  });

  const currentAmount = watch("currentAmount");
  const currentAgeTo = watch("currentAgeTo");
  const recommendedAmount = watch("recommendedAmount");
  const recommendedAgeTo = watch("recommendedAgeTo");
  const requiredBaseAmount = watch("requiredBaseAmount");
  const infinityRent = watch("infinityRent");
  const requiredCoverageCompensation = watch("requiredCoverageCompensation");

  // One queue per mounted risk (the list keys by guid), so a slower older update cannot overwrite
  // newer field values.
  const updateQueueRef = useRef<SerialQueue | null>(null);
  if (updateQueueRef.current === null) {
    updateQueueRef.current = createSerialQueue();
  }
  const enqueueUpdate = updateQueueRef.current;

  const onUpdate = async (updates: Partial<UpdateRiskBody>) => {
    // Snapshotted at blur time, so the queue applies the snapshots in the order of the edits.
    const data = buildUpdateRiskBody(
      risk,
      {
        currentAmount,
        currentAgeTo,
        recommendedAmount,
        recommendedAgeTo,
        requiredBaseAmount,
        infinityRent,
        requiredCoverageCompensation,
      },
      updates,
    );

    try {
      await enqueueUpdate(() =>
        updateInsuranceRisk({
          analysisId,
          versionId,
          clientGuid: clientId,
          riskGuid: risk.guid,
          data,
        }),
      );
    } catch {
      toast.error(t(Resources.Common.Error));
    }
  };

  // biome-ignore lint: <Open is required.>
  useEffect(() => {
    reset(defaultValues);
  }, [risk, reset]);

  return (
    <StyledStack spacing={Spacing.L}>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="body1" fontWeight={600}>
          {t(Resources.AnalysisDetail.Insurance.InsuranceRiskType[RISK_TYPE_RESOURCE_KEY[risk.riskType]])}
        </Typography>
        <ActionButton
          label={t(Resources.Common.Delete)}
          icon={<DeleteIcon />}
          tone="danger"
          onClick={async () => {
            try {
              await deleteInsuranceRisk({ analysisId, versionId, clientGuid: clientId, riskGuid: risk.guid });
            } catch {
              toast.error(t(Resources.Common.Error));
            }
          }}
        />
      </Box>
      <Grid container>
        <Grid size={5.25}>
          <Typography fontWeight={500} variant="body2" mb={Spacing.M}>
            {t(Resources.AnalysisDetail.Insurance.CurrentState)}
          </Typography>
          <Grid container gap={Spacing.M} display="flex">
            <Grid size={8.7}>
              <FormNumberField
                control={control}
                name="currentAmount"
                scale={2}
                thousandsSeparator=" "
                unit={t(Resources.Common.CZKSuffix)}
                onBlur={async () => {
                  await onUpdate({
                    currentAmount: {
                      value: currentAmount,
                      currencyCode: "CZK",
                      frequency: AmountFrequency.one_time,
                    },
                  });
                }}
                fullWidth
              />
            </Grid>
            <Grid size={3}>
              <FormNumberField
                control={control}
                name="currentAgeTo"
                scale={2}
                thousandsSeparator=" "
                unit={t(Resources.Common.Years)}
                onBlur={async () => {
                  await onUpdate({
                    currentAgeTo,
                  });
                }}
                fullWidth
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid size={1.5}>
          <Box display="flex" justifyContent="center" alignItems="flex-end" height="100%">
            <Box width={60} display="flex" justifyContent="space-between" alignItems="center" mb={Spacing.S}>
              <ThumbsUpIcon
                error={getThumbsUpColor(risk.currentAmount.value, risk.recommendedAmount.value, theme) === theme.palette.error.main}
                backgroundColor={getThumbsUpColor(risk.currentAmount.value, risk.recommendedAmount.value, theme)}
              />
              <ArrowRightIcon sx={{ fontSize: 18, color: colors.textSecondary }} />
            </Box>
          </Box>
        </Grid>
        <Grid size={5.25}>
          <Box display="flex" alignItems="center" mb={Spacing.S}>
            <Typography mr={Spacing.S} fontWeight={500} variant="body2">
              {t(Resources.AnalysisDetail.Insurance.RecommendedState)}
            </Typography>
            {risk.recommendedAmountLabels && risk.recommendedAmountLabels.length > 0 && (
              <RecommendedAmountsTooltip labels={risk.recommendedAmountLabels} />
            )}
          </Box>
          <Grid container gap={Spacing.M} display="flex">
            <Grid size={8.7}>
              <FormNumberField
                control={control}
                name="recommendedAmount"
                scale={2}
                thousandsSeparator=" "
                unit={t(Resources.Common.CZKSuffix)}
                disabled={readOnly}
                onBlur={async () => {
                  await onUpdate({
                    recommendedAmount: {
                      value: recommendedAmount,
                      currencyCode: "CZK",
                      frequency: AmountFrequency.one_time,
                    },
                  });
                }}
                fullWidth
              />
            </Grid>
            <Grid size={3}>
              <FormNumberField
                control={control}
                name="recommendedAgeTo"
                scale={2}
                thousandsSeparator=" "
                unit={t(Resources.Common.Years)}
                disabled={readOnly}
                onBlur={async () => {
                  await onUpdate({
                    recommendedAgeTo,
                  });
                }}
                fullWidth
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        {/* one wrapping row, so the chips line up across and between lines */}
        <Flex flexWrap="wrap" columnGap={Spacing.L} rowGap={Spacing.M}>
          {!parametersExpanded && risk.requiredBaseAmount.isVisible && (
            <Chip
              size="small"
              label={
                <>
                  {t(Resources.AnalysisDetail.Insurance.RequiredMinimum)}:{" "}
                  {formatCurrency(risk.requiredBaseAmount.value.value, risk.requiredBaseAmount.value.currencyCode)}
                </>
              }
            />
          )}
          {!parametersExpanded && risk.infinityRent.isVisible && (
            <Chip
              size="small"
              label={
                <>
                  {t(Resources.AnalysisDetail.Insurance.InfinityRent)}:{" "}
                  {formatCurrency(risk.infinityRent.value.value, risk.infinityRent.value.currencyCode)}
                </>
              }
            />
          )}
          {!parametersExpanded && risk.requiredCoverageCompensation.isVisible && (
            <Chip
              size="small"
              label={
                <>
                  {t(Resources.AnalysisDetail.Insurance.Compensation)}:{" "}
                  {formatCurrency(risk.requiredCoverageCompensation.value.value, risk.requiredCoverageCompensation.value.currencyCode)}/
                  {t(Resources.Common.Month)}
                </>
              }
            />
          )}
        </Flex>
        {risk.requiredBaseAmount.isVisible || risk.infinityRent.isVisible || risk.requiredCoverageCompensation.isVisible ? (
          <MuiButton sx={{ minWidth: "fit-content" }} onClick={() => setParametersExpanded(!parametersExpanded)}>
            {parametersExpanded ? (
              <>
                <Box display="flex" alignItems="center" mr={Spacing.S}>
                  <MinusIcon htmlColor={colors.textSecondary} />
                </Box>
                <Typography variant="body2" color="secondary" fontWeight={600}>
                  {t(Resources.AnalysisDetail.Insurance.HideCompensationParameters)}
                </Typography>
              </>
            ) : (
              <>
                <Box display="flex" alignItems="center" mr={Spacing.S}>
                  <AddIcon htmlColor={colors.textSecondary} />
                </Box>
                <Typography variant="body2" color="secondary" fontWeight={600}>
                  {t(Resources.AnalysisDetail.Insurance.ShowCompensationParameters)}
                </Typography>
              </>
            )}
          </MuiButton>
        ) : undefined}
      </Box>
      {parametersExpanded &&
      (risk.requiredBaseAmount.isVisible || risk.infinityRent.isVisible || risk.requiredCoverageCompensation.isVisible) ? (
        <Box sx={{ background: colors.backgroundTertiary, padding: Spacing.XL, borderRadius: Radius.L }}>
          <Grid container spacing={Spacing.L}>
            {risk.requiredBaseAmount.isVisible && (
              <Grid size={4}>
                <FormNumberField
                  control={control}
                  name="requiredBaseAmount"
                  label={t(Resources.AnalysisDetail.Insurance.InsuranceAmountRequiredMinimum)}
                  scale={2}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.CZKSuffix)}
                  disabled={readOnly}
                  onBlur={async () => {
                    if (requiredBaseAmount === risk.requiredBaseAmount.value.value) return;
                    await onUpdate({
                      requiredBaseAmount: {
                        value: {
                          value: requiredBaseAmount,
                          currencyCode: "CZK",
                          frequency: AmountFrequency.one_time,
                        },
                        hasChanged: true,
                      },
                    });
                  }}
                  fullWidth
                />
              </Grid>
            )}
            {risk.infinityRent.isVisible && (
              <Grid size={4}>
                <FormNumberField
                  control={control}
                  name="infinityRent"
                  label={t(Resources.AnalysisDetail.Insurance.InsuranceAmountTotalInfinitePension)}
                  scale={2}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.CZKSuffix)}
                  disabled={readOnly}
                  onBlur={async () => {
                    if (infinityRent === risk.infinityRent.value.value) return;
                    await onUpdate({
                      infinityRent: {
                        value: {
                          value: infinityRent,
                          currencyCode: "CZK",
                          frequency: AmountFrequency.one_time,
                        },
                        hasChanged: true,
                      },
                    });
                  }}
                  fullWidth
                />
              </Grid>
            )}
            {risk.requiredCoverageCompensation.isVisible && (
              <Grid size={4}>
                <FormNumberField
                  control={control}
                  name="requiredCoverageCompensation"
                  label={t(Resources.AnalysisDetail.Insurance.RequiredRiskCompensation)}
                  scale={2}
                  thousandsSeparator=" "
                  unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                  disabled={readOnly}
                  onBlur={async () => {
                    if (requiredCoverageCompensation === risk.requiredCoverageCompensation.value.value) return;
                    await onUpdate({
                      requiredCoverageCompensation: {
                        value: {
                          value: requiredCoverageCompensation,
                          currencyCode: "CZK",
                          frequency: AmountFrequency.one_time,
                        },
                        hasChanged: true,
                      },
                    });
                  }}
                  fullWidth
                />
              </Grid>
            )}
          </Grid>
        </Box>
      ) : undefined}
    </StyledStack>
  );
};
